import {BaseComponent, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {LastUpdated} from "@modules/PatientDataModule";
import {Card} from "../widget/Card";
import * as React from "react";
import {DB_Patient} from "@app/app-shared/patient";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {Api_CreatePcpContact, Api_GetPcpContact, Api_UpdatePcpContact, DB_Contact} from "@app/ir-q-app-common/types/db-contact";
import {Gender} from "@app/ir-q-app-common/types/contacts";
import {FONTS} from "@res/fonts";
import {DialogCard, showDialog} from "../widget/DialogCard";
import {ICONS} from "@res/icons";

type Props = {
    patient: DB_Patient
}
type State = {
    pcpContact: DB_Contact<"landline"> | null;
    loading: boolean;
};

type AddPcpContactDialogProps = {
    agentId: string
    onSuccess: (contact: DB_Contact<"landline">) => void
    onClose: () => void
    existingContact?: DB_Contact<"landline">
}

type AddPcpContactDialogState = {
    pcpName: string
    phoneNumber: string
    extension: string
    gender: Gender
    saving: boolean
}

const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "36px",
    border: "1px solid #D0D5DD",
    borderRadius: "4px",
    padding: "0 10px",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
};

const labelStyle: React.CSSProperties = {
    fontSize: "16px",
    fontWeight: 500,
    color: "#344054",
    marginBottom: "4px",
};

class AddPcpContactDialog
    extends BaseComponent<AddPcpContactDialogProps, AddPcpContactDialogState> {

    constructor(props: AddPcpContactDialogProps) {
        super(props);
        const {existingContact} = props;
        this.state = {
            pcpName: existingContact
                ? [existingContact.firstName, existingContact.lastName].filter(Boolean).join(" ")
                : "",
            phoneNumber: existingContact?.contactData.phoneNumber ?? "",
            extension: existingContact?.contactData.areaCode ?? "",
            gender: existingContact?.gender ?? Gender.Empty,
            saving: false,
        };
    }

    private handleSave = async () => {
        const {pcpName, phoneNumber, extension, gender} = this.state;
        const {agentId, existingContact} = this.props;
        const trimmed = pcpName.trim();
        const spaceIndex = trimmed.indexOf(" ");
        const firstName = spaceIndex === -1 ? trimmed : trimmed.slice(0, spaceIndex);
        const lastName = spaceIndex === -1 ? undefined : trimmed.slice(spaceIndex + 1) || undefined;
        const body = {firstName, lastName, phoneNumber, areaCode: extension, gender, agentId};

        this.setState({saving: true});
        try {
            const result = existingContact
                ? await XhrHttpModule
                    .createRequest<Api_UpdatePcpContact>(HttpMethod.POST, "update-pcp-contact")
                    .setRelativeUrl("/v2/contacts/update-pcp")
                    .setJsonBody(body)
                    .executeSync()
                : await XhrHttpModule
                    .createRequest<Api_CreatePcpContact>(HttpMethod.POST, "create-pcp-contact")
                    .setRelativeUrl("/v2/contacts/create-pcp")
                    .setJsonBody(body)
                    .executeSync();
            this.props.onSuccess(result);
        } finally {
            this.setState({saving: false});
        }
        return false;
    };

    render() {
        const {pcpName, phoneNumber, extension, gender, saving} = this.state;
        const content = (
            <div style={{display: "flex", flexDirection: "column", gap: "16px", padding: "8px 0 16px"}}>
                <div style={{display: "flex", flexDirection: "column"}}>
                    <span style={labelStyle}>PCP Name</span>
                    <input
                        style={inputStyle}
                        placeholder="Full name"
                        value={pcpName}
                        onChange={e => this.setState({pcpName: e.target.value})}
                    />
                </div>
                <div style={{display: "flex", gap: "16px"}}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                        <span style={labelStyle}>Phone Number</span>
                        <input
                            style={inputStyle}
                            placeholder="(555) 000-0000"
                            value={phoneNumber}
                            onChange={e => this.setState({phoneNumber: e.target.value})}
                        />
                    </div>
                    <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                        <span style={labelStyle}>Extension (opt.)</span>
                        <input
                            style={inputStyle}
                            placeholder="Ext."
                            value={extension}
                            onChange={e => this.setState({extension: e.target.value})}
                        />
                    </div>
                </div>
                <div style={{display: "flex", gap: "16px"}}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                        <span style={labelStyle}>Gender</span>
                        <select
                            style={inputStyle}
                            value={gender}
                            onChange={e => this.setState({gender: e.target.value as Gender})}
                        >
                            <option value={Gender.Empty}>Unknown</option>
                            <option value={Gender.Male}>Male</option>
                            <option value={Gender.Female}>Female</option>
                        </select>
                    </div>
                    <div style={{flex: 1}}/>
                </div>
            </div>
        );

        return <DialogCard
            showX={true}
            loading={saving}
            saveLabel={"Save"}
            cancelLabel={"Cancel"}
            title={this.props.existingContact ? "Edit PCP Contact" : "Add PCP Contact"}
            content={content}
            onSave={this.handleSave}
            onCancel={this.props.onClose}
            onClose={this.props.onClose}
            saveEnabled={!!pcpName.trim() && !!phoneNumber.trim()}
        />;
    }
}

export class PcpContactPanel
    extends BaseComponent<Props, State>
    implements LastUpdated {

    __lastUpdated = () => this.forceUpdate();
    constructor(props: Props) {
        super(props);
        this.state = {
            pcpContact: null,
            loading: true,
        };
    }
    componentDidMount() {
        this.fetchPcpContact();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.patient.unitId !== this.props.patient.unitId)
            this.fetchPcpContact();
    }

    fetchPcpContact = () => {
        this.setState({loading: true});
        XhrHttpModule
            .createRequest<Api_GetPcpContact>(HttpMethod.GET, "get-pcp-contact")
            .setRelativeUrl(`/v2/contacts/get-pcp?agentId=${this.props.patient.unitId}`)
            .setOnError(() => this.setState({pcpContact: null, loading: false}))
            .execute((contact) => this.setState({pcpContact: contact ?? null, loading: false}));
    }

    private renderModal = () => {
        return showDialog(
            <AddPcpContactDialog
                agentId={this.props.patient.unitId}
                existingContact={this.state.pcpContact ?? undefined}
                onSuccess={(contact) => this.setState({pcpContact: contact})}
                onClose={() => {}}
            />
        );
    }

    renderNoContact() {
        return <div>
            <Card style={{paddingBottom: "20px", paddingTop: "20px", gap: "16px", backgroundColor: "#FFF6D9", display: "flex", justifyContent: "center", alignItems: 'center',  flexDirection: "column"}}>
                {FONTS.reg_14_gray("No PCP Contact")}
                <button
                    style={{
                        border: "1px solid #026691",
                        backgroundColor: "white",
                        borderRadius: "4px",
                        width: "180px",
                        height: "29px",
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        this.renderModal()
                    }}
                >{FONTS.medium_16_primary_blue("Add PCP contact")}
                </button>
            </Card>
        </div>;
    }

    render() {
        if (this.state.loading) {
            return <div>
                <Card style={{paddingBottom: "20px"}}>
                    <h1 style={{position: "relative", left: "20px", top: "10px"}}>
                        {FONTS.reg_14_gray("loading...")}
                    </h1>
                </Card>
            </div>
        }

        if (!this.state.pcpContact) {
            return this.renderNoContact();
        }

        return <div>
            <Card style={{padding: "20px", paddingLeft: "24px", paddingRight: "24px", gap: "16px", display: "flex", flexDirection: "column" }}>
                {FONTS.bold_16("PCP Contact")}
                <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                    {FONTS.reg_16(`${this.state.pcpContact.firstName} ${this.state.pcpContact.lastName}`)}
                    {FONTS.reg_16_primary_dark_blue(this.state.pcpContact.contactData.phoneNumber)}
                    {<div style={{cursor: 'pointer'}} onClick={this.renderModal}>{ICONS.icon_edit("#026691", 16)}</div>}
                </div>
            </Card>
        </div>;

    }
}

